import { cloneElement, useState } from 'react'
import { FloatingPortal, autoUpdate, flip, offset, shift, size, useClick, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole } from '@floating-ui/react'

// Kebab menus appear in table rows, card headers and inline lists, so the popup
// can open anywhere on screen — including hard against an edge. It has to choose
// its own side.
//
// This used to hard-code placement 'left' with a hand-written offset() that did
// arithmetic against window.innerHeight. That only ever nudged the menu
// VERTICALLY: it never considered horizontal space, so a button near the left
// edge opened off-screen, and it never shortened a long list, so a tall menu ran
// past the bottom with no way to reach the items. flip and shift were already
// imported in this file — they were wired into Tooltip and never into Floating.
//
// The middleware does what the arithmetic was reaching for, against the real
// clipping boundary rather than the window:
//   flip  — swap to the opposite side when the preferred one does not fit
//   shift — slide along the cross axis to stay in view when neither side fits
//   size  — hand the content the space actually available (--floating-max-height)
//           so a long list stays reachable instead of overflowing off-screen
//
// The content does its own scrolling inside that height (menu.js's frame).
// This wrapper used to scroll itself: the menu's rounded border sat on the list
// inside it, so the corners scrolled away with the first and last items and
// the menu looked cut off (owner report 2026-10-13).
//
// bottom-end rather than left: a kebab sits at the right edge of its row or
// header, so opening downward and right-aligned is both the convention and the
// placement with the most room. flip handles the near-the-bottom case by
// opening upward instead.
export const Floating = ({ children, content, placement, isOpen, onOpenChange }) => {
  const { context, refs, floatingStyles } = useFloating({
    placement: placement || 'bottom-end',
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply ({ availableHeight, elements }) {
          elements.floating.style.setProperty('--floating-max-height', `${Math.max(120, availableHeight)}px`)
        }
      })
    ],
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: onOpenChange
  })

  const click = useClick(context)

  useDismiss(context, {
    enabled: true,
    escapeKey: true,
    outsidePress: true
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([click])

  return (
    <div>
      <div ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </div>
      {isOpen &&
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 1000 }} {...getFloatingProps()}>
            {content && cloneElement(content, {})}
          </div>
        </FloatingPortal>}
    </div>
  )
}

// Tooltip — hover/focus label built on the same floating-ui stack as Floating,
// but driven by useHover/useFocus instead of useClick. Used for the icon-only
// inline row actions so a bare icon still shows its word on hover.
// `keepCase` shows the label as written ("iOS", "visionOS"); by default a
// tooltip capitalizes each word.
export const Tooltip = ({ children, label, placement, disabled, keepCase }) => {
  const [open, setOpen] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: placement || 'top',
    middleware: [offset(6), flip(), shift({ padding: 6 })]
  })

  const hover = useHover(context, { move: false, delay: { open: 120, close: 0 } })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  return (
    <>
      <span ref={refs.setReference} {...getReferenceProps()} className="d-inline-flex align-items-center">
        {children}
      </span>
      {open && label && !disabled &&
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 1100 }}
            {...getFloatingProps()}
            role="tooltip"
            className={`bg-dark text-white px-2 py-1 rounded ${keepCase ? '' : 'text-capitalize'} text-nowrap`}>
            <small>{label}</small>
          </div>
        </FloatingPortal>}
    </>
  )
}
